import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { PaymentAccountSelect } from '@/components/travel/accounting/payment-account-select';

type PaymentMethod =
  | 'CASH'
  | 'CREDIT_CARD'
  | 'DEBIT_CARD'
  | 'BANK_TRANSFER'
  | 'PAYPAL'
  | 'CHEQUE'
  | 'OTHER';

const PAYMENT_METHOD_KEYS: PaymentMethod[] = [
  'CASH',
  'CREDIT_CARD',
  'DEBIT_CARD',
  'BANK_TRANSFER',
  'PAYPAL',
  'CHEQUE',
  'OTHER',
];

interface PaymentFormData {
  amount: number;
  paymentMethod: PaymentMethod;
  paymentDate: string;
  referenceNumber?: string;
  notes?: string;
}

interface PaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bookingId: string;
  currency: string;
  balanceDue: number;
  userId?: string;
  onSuccess?: () => void;
}

function getTodayString(): string {
  return new Date().toISOString().split('T')[0] ?? '';
}

export function PaymentModal({
  open,
  onOpenChange,
  bookingId,
  currency,
  balanceDue,
  userId,
  onSuccess,
}: PaymentModalProps) {
  const { t } = useTranslation('bookings');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accountId, setAccountId] = useState<string | undefined>(undefined);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<PaymentFormData>({
    defaultValues: {
      amount: balanceDue,
      paymentMethod: 'BANK_TRANSFER',
      paymentDate: getTodayString(),
    },
  });

  const utils = trpc.useUtils();

  const addPaymentMutation = trpc.booking.addPayment.useMutation({
    onSuccess: () => {
      utils.booking.byId.invalidate(bookingId);
      reset();
      setAccountId(undefined);
      onOpenChange(false);
      onSuccess?.();
      showSuccessToast(t('paymentModal.success'));
    },
    onError: (error) => {
      setIsSubmitting(false);
      showErrorToast(error.message || t('paymentModal.error'));
    },
  });

  const watchedAmount = watch('amount') || 0;

  const onSubmit = (data: PaymentFormData) => {
    setIsSubmitting(true);
    addPaymentMutation.mutate({
      bookingId,
      amount: data.amount,
      currency,
      paymentMethod: data.paymentMethod,
      paymentDate: new Date(data.paymentDate),
      referenceNumber: data.referenceNumber,
      notes: data.notes,
      userId,
      accountId,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('paymentModal.title')}</DialogTitle>
          <DialogDescription>
            {t('paymentModal.description', { balance: formatCurrency(balanceDue) })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="amount">{t('paymentModal.amount', { currency })} *</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0.01"
              max={balanceDue}
              {...register('amount', {
                required: t('paymentModal.amountRequired'),
                valueAsNumber: true,
                min: { value: 0.01, message: t('paymentModal.amountMin') },
                max: {
                  value: balanceDue,
                  message: t('paymentModal.amountMax', { max: formatCurrency(balanceDue) }),
                },
              })}
            />
            {errors.amount && <p className="text-sm text-red-500">{errors.amount.message}</p>}
            {watchedAmount === balanceDue && (
              <p className="text-xs text-green-600">{t('paymentModal.fullPaymentNote')}</p>
            )}
          </div>

          {/* Payment Method */}
          <div className="space-y-2">
            <Label>{t('paymentModal.paymentMethod')} *</Label>
            <Combobox
              options={PAYMENT_METHOD_KEYS.map((method) => ({
                value: method,
                label: t(`paymentMethodLabels.${method}`),
              }))}
              value={watch('paymentMethod') || 'BANK_TRANSFER'}
              onValueChange={(value) => setValue('paymentMethod', value as PaymentMethod)}
              placeholder={t('paymentModal.selectMethod')}
              searchPlaceholder={t('paymentModal.search')}
            />
          </div>

          {/* Cash / Bank Account (optional — auto by method mapping) */}
          <PaymentAccountSelect value={accountId} onChange={setAccountId} />

          {/* Payment Date */}
          <div className="space-y-2">
            <Label htmlFor="paymentDate">{t('paymentModal.paymentDate')} *</Label>
            <Input
              id="paymentDate"
              type="date"
              {...register('paymentDate', { required: t('paymentModal.dateRequired') })}
            />
            {errors.paymentDate && (
              <p className="text-sm text-red-500">{errors.paymentDate.message}</p>
            )}
          </div>

          {/* Reference Number */}
          <div className="space-y-2">
            <Label htmlFor="referenceNumber">{t('paymentModal.referenceNumber')}</Label>
            <Input
              id="referenceNumber"
              {...register('referenceNumber')}
              placeholder={t('paymentModal.referencePlaceholder')}
            />
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">{t('paymentModal.notes')}</Label>
            <Textarea
              id="notes"
              {...register('notes')}
              placeholder={t('paymentModal.notesPlaceholder')}
              rows={2}
            />
          </div>

          {/* Summary */}
          <div className="bg-muted rounded-lg p-3">
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('paymentModal.paymentAmount')}</span>
                <span className="font-medium">{formatCurrency(watchedAmount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('paymentModal.remainingBalance')}</span>
                <span
                  className={`font-medium ${balanceDue - watchedAmount <= 0 ? 'text-green-600' : ''}`}
                >
                  {formatCurrency(Math.max(0, balanceDue - watchedAmount))}
                </span>
              </div>
            </div>
          </div>

          {/* Error Messages */}
          {addPaymentMutation.error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {addPaymentMutation.error.message}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {t('paymentModal.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('paymentModal.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
